import React from "react";
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const TitleIdContext = React.createContext<string | undefined>(undefined);

/** Id som ModalShell pekar ut som dialogens namn. Rubriken i modalen ska använda det. */
export const useModalTitleId = (): string | undefined =>
  React.useContext(TitleIdContext);

interface ModalShellProps {
  onClose: () => void;
  dialogClassName?: string;
  children: React.ReactNode;
}

const isVisible = (element: HTMLElement): boolean =>
  element.getClientRects().length > 0;

export default function ModalShell({
  onClose,
  dialogClassName = "",
  children,
}: ModalShellProps): React.JSX.Element {
  const titleId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  // Läses vid första renderingen, innan rubriken i modalen hunnit ta fokus.
  const [opener] = React.useState(
    () => document.activeElement as HTMLElement | null,
  );
  // Hålls i en ref så att effekten inte körs om (och flyttar fokus) när förälderns
  // onClose byter identitet.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;

  // Ett klick räknas som "utanför" bara om både tryck och släpp skedde på ytan runt
  // dialogen, så att en markering som dras ut ur ett fält inte stänger modalen.
  const pressedOutsideRef = React.useRef(false);

  const isOutside = (target: EventTarget, overlay: HTMLElement): boolean =>
    target === overlay || target === dialogRef.current;

  React.useEffect(() => {
    document.body.classList.add("modal-open");
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      dialog.querySelector<HTMLElement>("h2")?.focus();
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialog) return;

      const nodes = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter(isVisible);
      if (nodes.length === 0) {
        event.preventDefault();
        return;
      }

      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      // Fokus har hamnat utanför modalen, till exempel efter ett klick på bakgrunden.
      if (!active || !dialog.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      // Rubriken är bara programmatiskt fokuserbar och räknas som startpunkt.
      const atStart = active === first || active.tagName === "H2";
      if (event.shiftKey && atStart) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("modal-open");
      opener?.focus?.();
    };
  }, [opener]);

  return (
    <TitleIdContext.Provider value={titleId}>
      <div className="modal-backdrop show" aria-hidden="true" />
      <div
        className="modal d-block"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => {
          pressedOutsideRef.current = isOutside(
            event.target,
            event.currentTarget,
          );
        }}
        onClick={(event) => {
          const startedOutside = pressedOutsideRef.current;
          pressedOutsideRef.current = false;
          if (startedOutside && isOutside(event.target, event.currentTarget)) {
            onCloseRef.current();
          }
        }}
      >
        <div
          className={
            "modal-dialog modal-dialog-centered modal-dialog-scrollable " +
            dialogClassName
          }
          ref={dialogRef}
        >
          {children}
        </div>
      </div>
    </TitleIdContext.Provider>
  );
}
